/**
 * Sub-view shown inside CreateTriggerDrawer when AutomationStore.isEditingScope
 * is true. Lets the user pick a Project, Registry, or Registry collection
 * scope for the new automation.
 *
 * Pending changes live locally and are only applied to EventStore when the
 * user clicks "Done". Cancel discards all pending changes.
 */

import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {type FormatOptionLabelMeta, type InputActionMeta} from 'react-select';
import {useShallow} from 'zustand/react/shallow';

import {
  ArtifactCollectionType,
  TriggerScopeType,
  useAutomationsProjectsQuery,
  useFetchOrgScopeTargetsQuery,
  useFetchRegistryProjectsPartialQuery,
  useProjectArtifactCollectionsQuery,
} from '../../../../generated/graphql';
import {useViewer} from '../../../../state/viewer/hooks';
import {propagateErrorsContext} from '../../../../util/errors';
import {useDebouncedMirror} from '../../../../util/hooks';
import {useStatsigGateAutomationEntityScope} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {useAutomationsOrgScope} from '../../../Automations/hooks/useAutomationsOrgScope';
import {type ScopeDisplayType} from '../../../Automations/tabs/scopeLabels';
import {TeamMemberRole} from '../../../MembersTable/TeamMembersTable/utils';
import {OrganizationMemberRole} from '../../../MembersTable/util';
import {checkRegistryProject} from '../../../Registries/common/utils';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {SemanticLoader as Loader} from '../../../utility/InstrumentedLoader';
import {useActionConfigStore} from './ActionStore';
import {AutomationStoreActions} from './AutomationStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {
  type AutomationsHubScopeKind,
  EventStoreActions,
  useEventStore,
} from './EventStore';
import {EntryPoint} from './types';

type ScopeKindOption = {
  value: AutomationsHubScopeKind;
  label: ScopeDisplayType;
  description: string;
};

const SCOPE_KIND_OPTIONS: ScopeKindOption[] = [
  {
    value: EntryPoint.ORGANIZATION,
    label: 'Organization',
    description:
      'Listen within the organization, including within its projects and registries.',
  },
  {
    value: EntryPoint.TEAM,
    label: 'Team',
    description: 'Listen within a team, including within its projects.',
  },
  {
    value: EntryPoint.PROJECT,
    label: 'Project',
    description: 'Listen within a single project.',
  },
  {
    value: EntryPoint.REGISTRY,
    label: 'Registry',
    description: 'Listen within a single registry.',
  },
  {
    value: EntryPoint.PORTFOLIO,
    label: 'Registry collection',
    description: 'Listen within a single registry collection.',
  },
];

// react-select renders the menu in a portal outside weave's <Tailwind> provider, so Tailwind
// utility classes don't apply here. Use the shared styled-component option styles instead.
// react-select reuses this renderer for the selected value in the closed control, so the
// description is dropped there ('value' context) to keep the control a single line.
const renderScopeKindOption = (
  option: ScopeKindOption,
  {context}: FormatOptionLabelMeta<ScopeKindOption>
) => (
  <S.SelectOptionLabel>
    <S.SelectOptionLabelText>{option.label}</S.SelectOptionLabelText>
    {context === 'menu' && (
      <S.SelectOptionSubLabelText>
        {option.description}
      </S.SelectOptionSubLabelText>
    )}
  </S.SelectOptionLabel>
);

type EntityOption = {
  value: string;
  label: string;
  entityName: string;
};

/**
 * Pending state held inside the sub-view until the user clicks Done. Every
 * field is optional because the user may change scope kinds before picking
 * a concrete target.
 */
type PendingScope = {
  scopeKind: AutomationsHubScopeKind | undefined;
  entityName: string | undefined;
  projectID: string | undefined;
  projectName: string | undefined;
  artifactCollectionID: string | undefined;
  artifactCollectionName: string | undefined;
  // Org/team (entity) scope: the picked org or team entity.
  entityScopeID: string | undefined;
  entityScopeName: string | undefined;
};

const EMPTY_PENDING: PendingScope = {
  scopeKind: undefined,
  entityName: undefined,
  projectID: undefined,
  projectName: undefined,
  artifactCollectionID: undefined,
  artifactCollectionName: undefined,
  entityScopeID: undefined,
  entityScopeName: undefined,
};

function isEntityScopeKind(
  scopeKind: AutomationsHubScopeKind | undefined
): scopeKind is EntryPoint.ORGANIZATION | EntryPoint.TEAM {
  return scopeKind === EntryPoint.ORGANIZATION || scopeKind === EntryPoint.TEAM;
}

function getInitialPending({
  automationsHubScopeKind: scopeKind,
  entityName,
  projectID,
  projectName,
  scopeID,
  scopeArtifactCollectionName,
}: {
  automationsHubScopeKind: AutomationsHubScopeKind | undefined;
  entityName: string | undefined;
  projectID: string | undefined;
  projectName: string | undefined;
  scopeID: string | undefined;
  scopeArtifactCollectionName: string | undefined;
}): PendingScope {
  if (!scopeKind) {
    return EMPTY_PENDING;
  }
  if (isEntityScopeKind(scopeKind)) {
    // For entity scope, `entityName` holds the org/team entity name and
    // `scopeID` the entity id (see setHubScope).
    return {
      ...EMPTY_PENDING,
      scopeKind,
      entityScopeID: scopeID,
      entityScopeName: entityName,
    };
  }
  if (scopeKind === EntryPoint.PORTFOLIO) {
    return {
      ...EMPTY_PENDING,
      scopeKind: EntryPoint.PORTFOLIO,
      entityName,
      projectID,
      projectName,
      artifactCollectionID: scopeID,
      artifactCollectionName: scopeArtifactCollectionName,
    };
  }
  return {
    ...EMPTY_PENDING,
    scopeKind,
    entityName,
    projectID,
    projectName,
  };
}

/**
 * Narrowed shape returned by {@link isPendingComplete}: every field needed to
 * dispatch `setHubScope` is non-null. PORTFOLIO additionally requires a
 * concrete artifact collection.
 */
type CompletePendingScope =
  | (PendingScope & {
      scopeKind: EntryPoint.ORGANIZATION | EntryPoint.TEAM;
      entityScopeID: string;
      entityScopeName: string;
    })
  | (PendingScope & {
      scopeKind: EntryPoint.PROJECT | EntryPoint.REGISTRY;
      entityName: string;
      projectID: string;
      projectName: string;
    })
  | (PendingScope & {
      scopeKind: EntryPoint.PORTFOLIO;
      entityName: string;
      projectID: string;
      projectName: string;
      artifactCollectionID: string;
    });

function isPendingComplete(
  pending: PendingScope
): pending is CompletePendingScope {
  const {scopeKind} = pending;
  if (!scopeKind) {
    return false;
  }
  if (isEntityScopeKind(scopeKind)) {
    return pending.entityScopeID != null && pending.entityScopeName != null;
  }
  const {entityName, projectID, projectName, artifactCollectionID} = pending;
  if (!entityName || !projectID || !projectName) {
    return false;
  }
  return scopeKind !== EntryPoint.PORTFOLIO || artifactCollectionID != null;
}

export const DrawerPageEditScope = () => {
  const eventConfig = useEventStore(
    useShallow(s => ({
      automationsHubScopeKind: s.automationsHubScopeKind,
      entityName: s.entityName,
      projectID: s.projectID,
      projectName: s.projectName,
      scopeID: s.scopeID,
      scopeArtifactCollectionName: s.scopeArtifactCollectionName,
    }))
  );

  const [pending, setPending] = useState<PendingScope>(() =>
    getInitialPending(eventConfig)
  );

  const {actionEntityName, setActionEntityName} = useActionConfigStore(
    useShallow(s => ({
      actionEntityName: s.entityName,
      setActionEntityName: s.setEntityName,
    }))
  );

  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgID = selectedAccount?.id ?? '';
  const orgName = selectedAccount?.name ?? '';

  const viewer = useViewer();
  const viewerUsername = viewer?.username ?? '';
  const viewerId = viewer?.id ?? '';

  // Org- and team-wide scopes are gated by Statsig; admin gating is layered on
  // top (org automations need org admin, team automations need team admin).
  const isEntityScopeEnabled = useStatsigGateAutomationEntityScope(orgID);

  // Org entity (Organization scope) and team entities (Team scope) for the
  // current org, plus the viewer's role used for admin gating. Both scopes map
  // to the backend ENTITY scope via their entity id. Fetched whenever entity
  // scope is enabled (not just after the user picks it) so we know up front
  // which options the viewer is allowed to see.
  const {orgName: hubOrgName} = useAutomationsOrgScope();
  const {data: orgScopeData, loading: orgScopeLoading} =
    useFetchOrgScopeTargetsQuery({
      variables: {orgName: hubOrgName, viewerUsername, viewerId},
      skip: hubOrgName === '' || !isEntityScopeEnabled || viewer == null,
      context: propagateErrorsContext(),
    });
  const orgEntity = orgScopeData?.organization?.orgEntity ?? null;
  const viewerIsOrgAdmin =
    orgScopeData?.organization?.member?.role === OrganizationMemberRole.ADMIN;

  // Only teams the viewer admins are eligible: creating a team automation
  // requires team admin. `role` is the legacy string; `memberRole.name` is the
  // RBAC name. Either can mark an admin, so check both (they can diverge).
  const teamOptions: EntityOption[] = useMemo(() => {
    const teams = orgScopeData?.organization?.teams ?? [];
    return teams.flatMap(team => {
      if (!team?.id || !team?.name) {
        return [];
      }
      const isAdmin =
        team.member?.role === TeamMemberRole.ADMIN ||
        team.member?.memberRole?.name === TeamMemberRole.ADMIN;
      return isAdmin
        ? [{value: team.id, label: team.name, entityName: team.name}]
        : [];
    });
  }, [orgScopeData]);

  const scopeKindOptions = useMemo(
    () =>
      SCOPE_KIND_OPTIONS.filter(opt => {
        if (!isEntityScopeKind(opt.value)) {
          return true;
        }
        if (!isEntityScopeEnabled) {
          return false;
        }
        // Organization needs org admin; Team needs admin of >=1 team.
        return opt.value === EntryPoint.ORGANIZATION
          ? viewerIsOrgAdmin
          : teamOptions.length > 0;
      }),
    [isEntityScopeEnabled, viewerIsOrgAdmin, teamOptions.length]
  );

  // Organization scope has no sub-target to pick: once "Organization" is chosen,
  // resolve the org entity automatically as soon as it loads.
  useEffect(() => {
    if (
      pending.scopeKind === EntryPoint.ORGANIZATION &&
      orgEntity != null &&
      pending.entityScopeID !== orgEntity.id
    ) {
      setPending(prev => ({
        ...prev,
        entityScopeID: orgEntity.id,
        entityScopeName: orgEntity.name ?? undefined,
      }));
    }
  }, [pending.scopeKind, pending.entityScopeID, orgEntity]);

  const handleScopeKindChange = (option: ScopeKindOption | null) => {
    if (option?.value && option.value !== pending.scopeKind) {
      setPending({...EMPTY_PENDING, scopeKind: option.value});
    }
  };

  const handleDone = () => {
    if (!isPendingComplete(pending)) {
      return;
    }
    switch (pending.scopeKind) {
      case EntryPoint.ORGANIZATION:
      case EntryPoint.TEAM:
        EventStoreActions.setHubScope({
          scopeKind: pending.scopeKind,
          scopeType: TriggerScopeType.Entity,
          scopeID: pending.entityScopeID,
          entityName: pending.entityScopeName,
        });
        // Default the action team to the scope team, but never overwrite a team
        // the user already picked for the action.
        if (pending.scopeKind === EntryPoint.TEAM && !actionEntityName) {
          setActionEntityName(pending.entityScopeName);
        }
        break;
      case EntryPoint.PORTFOLIO:
        EventStoreActions.setHubScope({
          scopeKind: EntryPoint.PORTFOLIO,
          scopeType: TriggerScopeType.ArtifactCollection,
          scopeID: pending.artifactCollectionID,
          entityName: pending.entityName,
          projectID: pending.projectID,
          projectName: pending.projectName,
          scopeArtifactCollectionName: pending.artifactCollectionName,
        });
        break;
      default:
        // isPendingComplete guarantees project fields are set for PROJECT/REGISTRY,
        // but TS can't fully narrow the intersection union here, so assert.
        EventStoreActions.setHubScope({
          scopeKind: pending.scopeKind,
          scopeType: TriggerScopeType.Project,
          scopeID: pending.projectID!,
          entityName: pending.entityName,
          projectID: pending.projectID,
          projectName: pending.projectName,
        });
        break;
    }
    AutomationStoreActions.closeEditScope();
  };

  const handleCancel = () => {
    AutomationStoreActions.closeEditScope();
  };

  const selectedScopeKindOption =
    scopeKindOptions.find(opt => opt.value === pending.scopeKind) ?? null;

  return (
    <S.CreateTriggerDrawer>
      <S.CenteredFlex>
        <Button
          icon="back"
          variant="ghost"
          size="large"
          onClick={handleCancel}
          aria-label="Back to event configuration"
        />
        <TailwindContents>
          <div className="text-sm font-semibold uppercase tracking-[0.5px] leading-[32px] py-20 text-moon-650 dark:text-wb-text-secondary">
            Edit scope
          </div>
        </TailwindContents>
        <S.CloseButton>
          <Button
            onClick={handleCancel}
            icon="close"
            size="large"
            variant="ghost"
            aria-label="Close edit scope"
          />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider />
      <S.CreateTriggerDrawerMain>
        <Tailwind>
          <div className="mb-16 text-base text-moon-650 dark:text-wb-text-secondary">
            Define at what level the event for this automation will be listened
            for.
          </div>
          <S.LabelRow>
            <S.Label>Scope</S.Label>
          </S.LabelRow>
          <Select<ScopeKindOption>
            options={scopeKindOptions}
            value={selectedScopeKindOption}
            placeholder="Select scope"
            onChange={handleScopeKindChange}
            formatOptionLabel={renderScopeKindOption}
          />
          {selectedScopeKindOption != null && (
            <div className="mt-4 text-sm text-moon-500 dark:text-wb-text-tertiary">
              {selectedScopeKindOption.description}{' '}
              {pending.scopeKind === EntryPoint.ORGANIZATION &&
                'Only organization admins can edit.'}
              {pending.scopeKind === EntryPoint.TEAM &&
                'Only team admins can edit.'}
            </div>
          )}

          {pending.scopeKind === EntryPoint.ORGANIZATION && (
            <>
              <S.LabelRow>
                <S.Label>Organization</S.Label>
              </S.LabelRow>
              <div className="flex min-w-0 items-center gap-8">
                <Icon
                  name="organization-corporate"
                  className="shrink-0 text-moon-500 dark:text-wb-text-tertiary"
                />
                <span
                  className="truncate font-semibold text-moon-600 dark:text-wb-text-secondary"
                  title={orgName}>
                  {orgName}
                </span>
              </div>
            </>
          )}

          {pending.scopeKind === EntryPoint.TEAM && (
            <ScopeChildPicker
              label="Team"
              placeholder="Select a team..."
              loading={orgScopeLoading}
              error={false}
              options={teamOptions}
              currentValue={pending.entityScopeID}
              onChange={option =>
                setPending({
                  ...EMPTY_PENDING,
                  scopeKind: EntryPoint.TEAM,
                  entityScopeID: option.value,
                  entityScopeName: option.entityName,
                })
              }
            />
          )}

          {pending.scopeKind === EntryPoint.PROJECT && (
            <HubProjectPicker
              orgID={orgID}
              pending={pending}
              onPick={picked =>
                setPending({
                  ...EMPTY_PENDING,
                  scopeKind: EntryPoint.PROJECT,
                  entityName: picked.entityName,
                  projectID: picked.projectID,
                  projectName: picked.projectName,
                })
              }
            />
          )}

          {pending.scopeKind === EntryPoint.REGISTRY && (
            <HubRegistryPicker
              orgName={orgName}
              pending={pending}
              onPick={picked =>
                setPending({
                  ...EMPTY_PENDING,
                  scopeKind: EntryPoint.REGISTRY,
                  entityName: picked.entityName,
                  projectID: picked.projectID,
                  projectName: picked.projectName,
                })
              }
            />
          )}

          {pending.scopeKind === EntryPoint.PORTFOLIO && (
            <HubRegistryCollectionPicker
              orgName={orgName}
              pending={pending}
              onPickRegistry={picked =>
                setPending({
                  ...EMPTY_PENDING,
                  scopeKind: EntryPoint.PORTFOLIO,
                  entityName: picked.entityName,
                  projectID: picked.projectID,
                  projectName: picked.projectName,
                })
              }
              onPickCollection={picked =>
                setPending(prev => ({
                  ...prev,
                  artifactCollectionID: picked.artifactCollectionID,
                  artifactCollectionName: picked.artifactCollectionName,
                }))
              }
            />
          )}
        </Tailwind>
      </S.CreateTriggerDrawerMain>
      <S.Divider />
      <S.Footer>
        <S.FooterLeftContent>
          <Button variant="secondary" size="large" onClick={handleCancel}>
            Cancel
          </Button>
        </S.FooterLeftContent>
        <S.FooterRightContent>
          <Button
            variant="primary"
            size="large"
            onClick={handleDone}
            disabled={!isPendingComplete(pending)}>
            Done
          </Button>
        </S.FooterRightContent>
      </S.Footer>
    </S.CreateTriggerDrawer>
  );
};

type ProjectOption = {
  value: string;
  label: string;
  entityName: string;
  projectName: string;
};

type ProjectPickerProps = {
  orgID: string;
  pending: PendingScope;
  onPick: (project: {
    entityName: string;
    projectID: string;
    projectName: string;
  }) => void;
};

const HubProjectPicker = ({orgID, pending, onPick}: ProjectPickerProps) => {
  // Orgs can have far more than `first` projects, so the picker searches
  // server-side: typed input is debounced, then sent as `pattern` (a
  // substring match on project name per the gorilla resolver).
  const {inputValue, pattern, onInputChange} = useServerSearchInput();
  const {data, previousData, loading, error} = useAutomationsProjectsQuery({
    variables: {
      orgID,
      first: 100,
      pattern,
    },
    skip: orgID === '',
    context: propagateErrorsContext(),
  });

  // `data` resets to undefined while a new pattern loads; keep showing the
  // previous results so the Select doesn't unmount mid-typing.
  const projectsData = data ?? previousData;
  const options: ProjectOption[] = useMemo(() => {
    const edges = projectsData?.organization?.projects?.edges ?? [];
    return edges.flatMap(({node}) => {
      if (!node || !node.entityName || !node.name) {
        return [];
      }
      const {id, entityName, name: projectName} = node;
      return [
        {
          value: id,
          label: `${entityName}/${projectName}`,
          entityName,
          projectName,
        },
      ];
    });
  }, [projectsData]);

  const selectedOption: ProjectOption | undefined = useMemo(() => {
    const {projectID, entityName, projectName} = pending;
    if (!projectID || !entityName || !projectName) {
      return undefined;
    }
    return {
      value: projectID,
      label: `${entityName}/${projectName}`,
      entityName,
      projectName,
    };
  }, [pending]);

  return (
    <ScopeChildPicker
      label="Project"
      placeholder="Select a project..."
      loading={loading && data == null && previousData == null}
      searching={loading}
      error={error != null && projectsData == null}
      options={options}
      currentValue={pending.projectID}
      selectedOption={selectedOption}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onChange={option => {
        onPick({
          entityName: option.entityName,
          projectID: option.value,
          projectName: option.projectName,
        });
      }}
    />
  );
};

type RegistryOption = {
  value: string;
  label: string;
  entityName: string;
  projectName: string;
};

type RegistryEdge = {
  node?: {
    id: string;
    entityName?: string | null;
    name?: string | null;
  } | null;
};

/**
 * Registry projects come back named `wandb-registry-<name>`; users pick and
 * read the bare registry name, matching the hub table's Scope cell. Nodes
 * that aren't registry projects are dropped.
 */
export function toRegistryOptions(
  edges: readonly RegistryEdge[]
): RegistryOption[] {
  return edges.flatMap(({node}) => {
    if (!node || !node.entityName || !node.name) {
      return [];
    }
    const {registryName} = checkRegistryProject(node.name);
    if (registryName == null) {
      return [];
    }
    const {id, entityName, name: projectName} = node;
    return [{value: id, label: registryName, entityName, projectName}];
  });
}

function toSelectedRegistryOption(
  pending: PendingScope
): RegistryOption | undefined {
  const {projectID, entityName, projectName} = pending;
  if (!projectID || !entityName || !projectName) {
    return undefined;
  }
  return {
    value: projectID,
    label: checkRegistryProject(projectName).registryName ?? projectName,
    entityName,
    projectName,
  };
}

type RegistryPickerProps = {
  orgName: string;
  pending: PendingScope;
  onPick: (registry: {
    entityName: string;
    projectID: string;
    projectName: string;
  }) => void;
};

const HubRegistryPicker = ({orgName, pending, onPick}: RegistryPickerProps) => {
  // Server-side search like the project picker: `pattern` is matched against
  // registry names (no `wandb-registry-` prefix — the resolver applies it).
  const {inputValue, pattern, onInputChange} = useServerSearchInput();
  const {data, previousData, loading, error} =
    useFetchRegistryProjectsPartialQuery({
      variables: {orgName, pattern, limit: 100},
      skip: orgName === '',
      context: propagateErrorsContext(),
    });

  const registriesData = data ?? previousData;
  const options: RegistryOption[] = useMemo(
    () =>
      toRegistryOptions(registriesData?.organization?.registries?.edges ?? []),
    [registriesData]
  );

  return (
    <ScopeChildPicker
      label="Registry"
      placeholder="Select a registry..."
      loading={loading && data == null && previousData == null}
      searching={loading}
      error={error != null && registriesData == null}
      options={options}
      currentValue={pending.projectID}
      selectedOption={toSelectedRegistryOption(pending)}
      inputValue={inputValue}
      onInputChange={onInputChange}
      onChange={option =>
        onPick({
          entityName: option.entityName,
          projectID: option.value,
          projectName: option.projectName,
        })
      }
    />
  );
};

type CollectionOption = {
  value: string;
  label: string;
  artifactCollectionID: string;
  artifactCollectionName: string;
};

type RegistryCollectionPickerProps = {
  orgName: string;
  pending: PendingScope;
  onPickRegistry: (registry: {
    entityName: string;
    projectID: string;
    projectName: string;
  }) => void;
  onPickCollection: (collection: {
    artifactCollectionID: string;
    artifactCollectionName: string;
  }) => void;
};

const HubRegistryCollectionPicker = ({
  orgName,
  pending,
  onPickRegistry,
  onPickCollection,
}: RegistryCollectionPickerProps) => {
  const {
    inputValue: registryInputValue,
    pattern: registryPattern,
    onInputChange: onRegistryInputChange,
  } = useServerSearchInput();
  const {
    data: registryData,
    previousData: registryPreviousData,
    loading: registryLoading,
  } = useFetchRegistryProjectsPartialQuery({
    variables: {orgName, pattern: registryPattern, limit: 100},
    skip: orgName === '',
    context: propagateErrorsContext(),
  });

  const registriesData = registryData ?? registryPreviousData;
  const registryOptions: RegistryOption[] = useMemo(
    () =>
      toRegistryOptions(registriesData?.organization?.registries?.edges ?? []),
    [registriesData]
  );

  const {data: collectionData, loading: collectionLoading} =
    useProjectArtifactCollectionsQuery({
      variables: {
        entityName: pending.entityName ?? '',
        projectName: pending.projectName ?? '',
        collectionTypes: [ArtifactCollectionType.Portfolio],
      },
      skip: pending.entityName == null || pending.projectName == null,
      context: propagateErrorsContext(),
    });

  const collectionOptions: CollectionOption[] = useMemo(() => {
    const artifactTypeEdges =
      collectionData?.project?.artifactTypes.edges ?? [];
    return artifactTypeEdges.flatMap(({node: typeNode}) => {
      const collectionEdges = typeNode?.artifactCollections?.edges ?? [];
      return collectionEdges.flatMap(({node}) => {
        if (!node) {
          return [];
        }
        const {id, name: artifactCollectionName} = node;
        return [
          {
            value: id,
            label: artifactCollectionName,
            artifactCollectionID: id,
            artifactCollectionName,
          },
        ];
      });
    });
  }, [collectionData]);

  return (
    <>
      <ScopeChildPicker
        label="Registry"
        placeholder="Select a registry..."
        loading={
          registryLoading &&
          registryData == null &&
          registryPreviousData == null
        }
        searching={registryLoading}
        error={false}
        options={registryOptions}
        currentValue={pending.projectID}
        selectedOption={toSelectedRegistryOption(pending)}
        inputValue={registryInputValue}
        onInputChange={onRegistryInputChange}
        onChange={option =>
          onPickRegistry({
            entityName: option.entityName,
            projectID: option.value,
            projectName: option.projectName,
          })
        }
      />
      {pending.projectID != null && (
        <ScopeChildPicker
          label="Collection"
          placeholder="Select a collection..."
          loading={collectionLoading}
          error={false}
          options={collectionOptions}
          currentValue={pending.artifactCollectionID}
          onChange={option =>
            onPickCollection({
              artifactCollectionID: option.artifactCollectionID,
              artifactCollectionName: option.artifactCollectionName,
            })
          }
        />
      )}
    </>
  );
};

/**
 * Shared server-side search wiring for the pickers below. Returns the live
 * input text, a debounced `pattern` for the query (null when empty), and an
 * `onInputChange` that keeps typed text on 'input-change' and clears it on
 * any other action (selection, menu close) so the next open starts
 * unfiltered.
 */
function useServerSearchInput() {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebouncedMirror(inputValue.trim(), 300);
  const onInputChange = (value: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(value);
    } else {
      setInputValue('');
    }
  };
  return {inputValue, pattern: debouncedInput || null, onInputChange};
}

type ScopeChildPickerProps<TOption extends {value: string; label: string}> = {
  label: string;
  placeholder: string;
  loading: boolean;
  error: boolean;
  options: TOption[];
  currentValue: string | undefined;
  onChange: (option: TOption) => void;
  // Server-side search wiring: when `onInputChange` is set, the caller owns
  // filtering, so the control keeps the menu's loading indicator instead of
  // replacing the picker with a full loader on every keystroke, and client
  // filtering is disabled (`filterOption={null}`).
  inputValue?: string;
  onInputChange?: (value: string, actionMeta: InputActionMeta) => void;
  searching?: boolean;
  // Fallback for the selected value when the current search results don't
  // include it — without this, an in-flight server search blanks the control.
  selectedOption?: TOption;
};

export function ScopeChildPicker<
  TOption extends {value: string; label: string},
>({
  label,
  placeholder,
  loading,
  error,
  options,
  currentValue,
  onChange,
  inputValue,
  onInputChange,
  searching,
  selectedOption,
}: ScopeChildPickerProps<TOption>) {
  if (loading) {
    return (
      <>
        <S.LabelRow>
          <S.Label>{label}</S.Label>
        </S.LabelRow>
        <Loader active inline name="hub-scope-picker-loader" size="tiny" />
      </>
    );
  }
  if (error) {
    return (
      <>
        <S.LabelRow>
          <S.Label>{label}</S.Label>
        </S.LabelRow>
        <div className="text-red-500">Failed to load {label.toLowerCase()}</div>
      </>
    );
  }
  const selected =
    options.find(o => o.value === currentValue) ?? selectedOption ?? null;
  return (
    <>
      <S.LabelRow>
        <S.Label>{label}</S.Label>
      </S.LabelRow>
      <Select<TOption>
        options={options}
        value={selected}
        placeholder={placeholder}
        isLoading={searching}
        inputValue={inputValue}
        onInputChange={onInputChange}
        filterOption={onInputChange != null ? null : undefined}
        onChange={(option: TOption | null) => {
          if (option != null) {
            onChange(option);
          }
        }}
      />
    </>
  );
}
